import React from 'react';
import type { Components } from 'react-markdown';

import { cn } from '@cineamo/ui/src/helpers/cn';

import CinemaDescriptionMarkdown from '@cineamo/ui/src/markdown/CinemaDescriptionMarkdown';
import ExpandableText from '@cineamo/ui/src/misc/ExpandableText';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

type MovieOverviewProps = {
    // Optional: a movie can carry no overview for the requested locale; the component
    // already renders nothing in that case.
    overview?: string;
    className?: string;
    maxLines?: number;
    /** Render `overview` as markdown (custom overrides); the TMDB text stays plain. */
    markdown?: boolean;
};

// Inherit the header's text size instead of the description defaults.
const markdownComponents: Partial<Components> = {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    p: ({ node, ...props }) => <p {...props} />,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    li: ({ node, ...props }) => <li {...props} />
};

const MovieOverview: React.FC<MovieOverviewProps> = ({ overview, className, maxLines = 5, markdown = false }) => {
    const { track } = useAnalytics();
    function onExpandOverview() {
        track('Movie Overview Text Expanded');
    }
    if (!overview || overview.length === 0) return null;
    return (
        <ExpandableText
            className={cn('pointer-events-auto', className)}
            classNameButton="text-[var(--primary-color)] underline italic font-bold !opacity-100"
            maxLines={maxLines}
            text={
                markdown ? (
                    // ExpandableText is whitespace-pre-wrap; markdown output carries its own spacing.
                    <CinemaDescriptionMarkdown
                        className="whitespace-normal space-y-8"
                        description={overview}
                        allowInlineHtml
                        components={markdownComponents}
                    />
                ) : (
                    overview
                )
            }
            onClick={onExpandOverview}
            initiallyExpanded={false}
            expandOnTextClick={true}
        />
    );
};

export default MovieOverview;
